import { Dialog as BaseDialog } from '@base-ui/react/dialog';

import ArrowLeftIcon from '@/assets/svg/ui-elements/chevron-left.svg';
import type {
  PipelineCard,
  PipelineCardDetail as PipelineCardDetailDto,
} from '@/pages/pipelines/common/types';

import {
  DesktopHeaderActions,
  DetailBadgeRow,
  DetailDivider,
  DetailHeader,
  DetailPillButton,
  DetailTypeBadge,
  HeaderContent,
  HeaderLine,
  HeaderMeta,
  HeaderMetaLink,
  HeaderTimerBadge,
  HeaderTitle,
  HeaderTitleLink,
  MobileCloseButton,
} from './styled';

type DetailAction = PipelineCardDetailDto['headerActions'][number];

type Props = {
  detail: PipelineCardDetailDto;
  title: string;
  badges: PipelineCard['badges'];
  headerAction?: DetailAction;
  inlineMetaAction?: DetailAction;
  onNavigate: (_href: string | null | undefined) => void;
};

export const PipelineDetailHeader = ({
  detail,
  title,
  badges,
  headerAction,
  inlineMetaAction,
  onNavigate,
}: Props) => (
  <>
    <DetailHeader>
      <BaseDialog.Close
        render={
          <MobileCloseButton
            aria-label="Close pipeline detail"
            size="xs"
            color="tertiary"
            variant="ghost"
          >
            <ArrowLeftIcon width={24} height={24} />
          </MobileCloseButton>
        }
      />
      <HeaderTitle>{renderHeaderTitle(title, detail.headerLinks?.titleHref)}</HeaderTitle>
      <DesktopHeaderActions>
        {headerAction && (
          <DetailPillButton
            color="tertiary"
            variant="ghost"
            size="sm"
            disabled={headerAction.disabled || !headerAction.href}
            onClick={() => onNavigate(headerAction.href)}
          >
            {headerAction.label}
          </DetailPillButton>
        )}
        {detail.timer && (
          <HeaderTimerBadge data-tone={detail.timer.tone}>{detail.timer.label}</HeaderTimerBadge>
        )}
      </DesktopHeaderActions>
    </DetailHeader>

    <DetailDivider />

    {(detail.subtitleLines.length > 0 || badges.length > 0) && (
      <HeaderContent>
        {detail.subtitleLines[0] && (
          <HeaderLine>
            {renderHeaderMeta(detail.subtitleLines[0], detail.headerLinks?.subtitleLineHrefs?.[0])}
            {inlineMetaAction && (
              <DetailPillButton
                color="tertiary"
                variant="ghost"
                size="sm"
                disabled={inlineMetaAction.disabled || !inlineMetaAction.href}
                onClick={() => onNavigate(inlineMetaAction.href)}
              >
                {inlineMetaAction.label}
              </DetailPillButton>
            )}
          </HeaderLine>
        )}

        {detail.subtitleLines.slice(1).map((line, index) => (
          <HeaderMeta key={`${index}-${line}`}>
            {renderHeaderMetaContent(line, detail.headerLinks?.subtitleLineHrefs?.[index + 1])}
          </HeaderMeta>
        ))}

        {badges.length > 0 && (
          <DetailBadgeRow>
            {badges.map((badge) => (
              <DetailTypeBadge
                key={badge.key}
                data-tone={badge.tone}
                data-variant={badge.variant ?? 'filled'}
              >
                {badge.label}
              </DetailTypeBadge>
            ))}
          </DetailBadgeRow>
        )}
      </HeaderContent>
    )}
  </>
);

const renderHeaderTitle = (title: string, href: string | null | undefined) => {
  if (!href) return title;

  return (
    <HeaderTitleLink to={href} size="mini">
      {title}
    </HeaderTitleLink>
  );
};

const renderHeaderMeta = (line: string, href: string | null | undefined) => (
  <HeaderMeta>{renderHeaderMetaContent(line, href)}</HeaderMeta>
);

const renderHeaderMetaContent = (line: string, href: string | null | undefined) => {
  if (!href) return line;

  const viaPrefix = 'via ';

  if (line.startsWith(viaPrefix)) {
    return (
      <>
        {viaPrefix}
        <HeaderMetaLink to={href} size="mini">
          {line.slice(viaPrefix.length)}
        </HeaderMetaLink>
      </>
    );
  }

  return (
    <HeaderMetaLink to={href} size="mini">
      {line}
    </HeaderMetaLink>
  );
};
